// Kaynak ve sayfa tanım drawer'ları.
//
// Kaynak = ProView paketindeki `site` (bir SCADA operatör bilgisayarı),
// sayfa   = paketteki `page.id` (tanınan SCADA ekranı).
//
// Kod alanı YALNIZ açılışta yazılabilir: ajan konfigürasyonundaki `site` ve uç
// nokta URL'i bu kodu taşıyor; sonradan değiştirmek sahadaki ajanı sessizce
// koparırdı. Sunucu da PUT'ta kodu okumuyor (YonetimUclari.KaynakGuncelle).

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import {
  entegrasyon,
  type EntegrasyonDurumu,
  type KaynakSatiri,
  type SayfaSatiri,
} from './api'

/** Sunucudaki SlugKalibi ile aynı (YonetimUclari.cs:29) — 409 beklemeden uyarır. */
const SLUG = /^[a-z0-9_-]{2,64}$/

const SLUG_IPUCU = 'Küçük harf, rakam, "-" ve "_" · 2–64 karakter'

function DurumSecici({
  deger,
  degisti,
  aciklama,
}: {
  deger: EntegrasyonDurumu
  degisti: (d: EntegrasyonDurumu) => void
  aciklama: string
}) {
  return (
    <div className="fld">
      <label>Durum</label>
      <select
        className="in"
        value={deger}
        onChange={(e) => degisti(e.target.value as EntegrasyonDurumu)}
      >
        <option value="aktif">Aktif</option>
        <option value="pasif">Pasif</option>
      </select>
      <div className="fld-hint">{aciklama}</div>
    </div>
  )
}

// ------------------------------------------------------------------- kaynak

export function KaynakDrawer({ mevcut }: { mevcut?: KaynakSatiri }) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [kod, setKod] = useState(mevcut?.kod ?? '')
  const [ad, setAd] = useState(mevcut?.ad ?? '')
  const [aciklama, setAciklama] = useState(mevcut?.aciklama ?? '')
  const [durum, setDurum] = useState<EntegrasyonDurumu>(mevcut?.durum ?? 'aktif')

  const kodGecerli = SLUG.test(kod)

  const kaydet = useMutation({
    mutationFn: () =>
      mevcut
        ? entegrasyon.kaynakGuncelle(mevcut.id, {
            ad: ad.trim(),
            aciklama: aciklama.trim() || null,
            durum,
          })
        : entegrasyon.kaynakOlustur({
            kod: kod.trim(),
            ad: ad.trim(),
            aciklama: aciklama.trim() || null,
          }),
    onSuccess: (k) => {
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynaklar'] })
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynak', k.id] })
      toast(
        mevcut
          ? `${k.kod} · kaynak güncellendi`
          : `${k.kod} · kaynak açıldı — anahtar üretmeden paket kabul edilmez`,
      )
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Kaynak kaydedilemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Kaynak, ajanın gönderdiği <b>site</b> alanına karşılık gelir — bir SCADA
            operatör bilgisayarı. Uç nokta adresi ve API anahtarı kaynak açıldıktan
            sonra üretilir.
          </div>
        </div>

        <div className="fgrid">
          <div className={kod && !kodGecerli ? 'fld err' : 'fld'}>
            <label>Kod</label>
            <input
              className="in mono"
              value={kod}
              readOnly={!!mevcut}
              placeholder="asya-biyogaz"
              onChange={(e) => setKod(e.target.value)}
            />
            <div className="fld-hint">
              {mevcut
                ? 'Kod değişmez — ajan konfigürasyonundaki site ve URL bunu taşıyor'
                : SLUG_IPUCU}
            </div>
          </div>
          <div className="fld">
            <label>Ad</label>
            <input
              className="in"
              value={ad}
              placeholder="Asya Biyogaz · Operatör PC"
              onChange={(e) => setAd(e.target.value)}
            />
          </div>
        </div>

        <div className="fld" style={{ marginTop: 8 }}>
          <label>Açıklama</label>
          <input
            className="in"
            value={aciklama}
            placeholder="Konum, sorumlu, ajan sürümü notu…"
            onChange={(e) => setAciklama(e.target.value)}
          />
        </div>

        {mevcut && (
          <div style={{ marginTop: 8 }}>
            <DurumSecici
              deger={durum}
              degisti={setDurum}
              aciklama="Pasif kaynağın anahtarları 401 alır — ajan paketi ölü mektuba düşürür"
            />
          </div>
        )}
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={kaydet.isPending || !ad.trim() || (!mevcut && !kodGecerli)}
          title={
            !ad.trim()
              ? 'Ad zorunlu'
              : !mevcut && !kodGecerli
                ? SLUG_IPUCU
                : undefined
          }
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : mevcut ? 'Kaydet' : 'Kaynağı aç'}
        </button>
      </DrawerAltlik>
    </>
  )
}

// -------------------------------------------------------------------- sayfa

export function SayfaDrawer({
  kaynakId,
  mevcut,
}: {
  kaynakId: string
  mevcut?: SayfaSatiri
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [sayfaKodu, setSayfaKodu] = useState(mevcut?.sayfaKodu ?? '')
  const [ad, setAd] = useState(mevcut?.ad ?? '')
  const [aciklama, setAciklama] = useState(mevcut?.aciklama ?? '')
  const [durum, setDurum] = useState<EntegrasyonDurumu>(mevcut?.durum ?? 'aktif')

  const kodGecerli = SLUG.test(sayfaKodu)

  const kaydet = useMutation({
    mutationFn: () =>
      mevcut
        ? entegrasyon.sayfaGuncelle(mevcut.id, {
            ad: ad.trim(),
            aciklama: aciklama.trim() || null,
            durum,
          })
        : entegrasyon.sayfaOlustur(kaynakId, {
            sayfaKodu: sayfaKodu.trim(),
            ad: ad.trim(),
            aciklama: aciklama.trim() || null,
          }),
    onSuccess: (s) => {
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynaklar'] })
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynak', kaynakId] })
      kuyruk.invalidateQueries({ queryKey: ['entegrasyon-sayfa', s.id] })
      toast(`${s.sayfaKodu} · sayfa ${mevcut ? 'güncellendi' : 'tanımlandı'}`)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Sayfa kaydedilemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Sayfa kodu, paketteki <b>page.id</b> ile birebir aynı olmalı. Ajan tanımlı
            olmayan bir kodla paket gönderirse sayfa <b>kendiliğinden</b> açılır — bu
            form yalnız kodu önceden bilip adlandırmak istediğinizde gerekir.
          </div>
        </div>

        <div className="fgrid">
          <div className={sayfaKodu && !kodGecerli ? 'fld err' : 'fld'}>
            <label>Sayfa kodu</label>
            <input
              className="in mono"
              value={sayfaKodu}
              readOnly={!!mevcut}
              placeholder="genel_bakis"
              onChange={(e) => setSayfaKodu(e.target.value)}
            />
            <div className="fld-hint">
              {mevcut ? 'Kod değişmez — paketin page.id alanıyla eşleşiyor' : SLUG_IPUCU}
            </div>
          </div>
          <div className="fld">
            <label>Ad</label>
            <input
              className="in"
              value={ad}
              placeholder="Genel Bakış"
              onChange={(e) => setAd(e.target.value)}
            />
          </div>
        </div>

        <div className="fld" style={{ marginTop: 8 }}>
          <label>Açıklama</label>
          <input
            className="in"
            value={aciklama}
            placeholder="Bu mimikte hangi üniteler görünüyor…"
            onChange={(e) => setAciklama(e.target.value)}
          />
        </div>

        {mevcut && (
          <div style={{ marginTop: 8 }}>
            <DurumSecici
              deger={durum}
              degisti={setDurum}
              aciklama="Pasif sayfa okuma almaya devam eder; yalnız listede geri plana düşer"
            />
          </div>
        )}

        {mevcut?.otomatikMi && (
          <div className="rule" style={{ marginTop: 10 }}>
            <b>Otomatik açıldı</b>
            <span>
              Bu sayfa ilk pakette kendiliğinden oluştu. Kaydettiğinizde “kullanıcı
              adlandırdı” olarak işaretlenir.
            </span>
          </div>
        )}
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={kaydet.isPending || !ad.trim() || (!mevcut && !kodGecerli)}
          title={
            !ad.trim()
              ? 'Ad zorunlu'
              : !mevcut && !kodGecerli
                ? SLUG_IPUCU
                : undefined
          }
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : mevcut ? 'Kaydet' : 'Sayfayı tanımla'}
        </button>
      </DrawerAltlik>
    </>
  )
}
